import { adminFetch } from "./base";

// ---------------------------------------------------------------------------
// Shared helpers
// ---------------------------------------------------------------------------

function toQueryString(params: Record<string, unknown>): string {
  const entries = Object.entries(params).filter(
    ([, v]) => v != null && v !== "",
  );
  if (entries.length === 0) return "";
  const qs = new URLSearchParams(entries.map(([k, v]) => [k, String(v)]));
  return `?${qs.toString()}`;
}

// ---------------------------------------------------------------------------
// Pagination
// ---------------------------------------------------------------------------

export interface Pagination {
  readonly page: number;
  readonly limit: number;
  readonly total: number;
}

export interface ListParams {
  readonly page?: number;
  readonly limit?: number;
}

// ---------------------------------------------------------------------------
// Promotions
// ---------------------------------------------------------------------------

export type PromoType =
  | "percentage"
  | "fixed_amount"
  | "free_shipping"
  | "buy_x_get_y";

export type PromoStatus = "active" | "scheduled" | "expired" | "paused";

export interface Promotion {
  readonly id: string;
  readonly name: string;
  readonly type: PromoType;
  readonly status: PromoStatus;
  readonly discount_value: number;
  readonly min_order_fcfa: number | null;
  readonly max_uses: number | null;
  readonly uses_count: number;
  readonly starts_at: string;
  readonly ends_at: string | null;
  readonly applicable_products: readonly string[];
  readonly applicable_categories: readonly string[];
  readonly created_at: string;
}

export interface PromotionListResponse {
  readonly promotions: readonly Promotion[];
  readonly pagination: Pagination;
}

export interface PromotionListParams extends ListParams {
  readonly type?: PromoType | null;
  readonly status?: PromoStatus | null;
  readonly date_from?: string | null;
  readonly date_to?: string | null;
}

export interface CreatePromotionInput {
  readonly name: string;
  readonly type: PromoType;
  readonly discount_value: number;
  readonly min_order_fcfa?: number | null;
  readonly max_uses?: number | null;
  readonly starts_at: string;
  readonly ends_at?: string | null;
  readonly applicable_products?: readonly string[];
  readonly applicable_categories?: readonly string[];
}

// ---------------------------------------------------------------------------
// Coupons
// ---------------------------------------------------------------------------

export type CouponStatus = "active" | "used" | "expired" | "revoked";

export interface Coupon {
  readonly id: string;
  readonly code: string;
  readonly promotion_id: string;
  readonly promotion_name: string;
  readonly status: CouponStatus;
  readonly max_uses: number | null;
  readonly uses_count: number;
  readonly created_at: string;
}

export interface CouponListResponse {
  readonly coupons: readonly Coupon[];
  readonly pagination: Pagination;
}

export interface CouponListParams extends ListParams {
  readonly status?: CouponStatus | null;
  readonly promotion_id?: string | null;
}

export interface CreateCouponInput {
  readonly code: string;
  readonly promotion_id: string;
  readonly max_uses?: number | null;
}

export interface BulkCreateCouponsInput {
  readonly promotion_id: string;
  readonly prefix: string;
  readonly count: number;
  readonly max_uses_each?: number | null;
}

// ---------------------------------------------------------------------------
// Flash Sales
// ---------------------------------------------------------------------------

export type FlashSaleStatus = "upcoming" | "active" | "ended";

export interface FlashSale {
  readonly id: string;
  readonly name: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly status: FlashSaleStatus;
  readonly discount_percent: number;
  readonly product_ids: readonly string[];
  readonly product_count: number;
  readonly total_orders: number;
}

export interface FlashSaleListResponse {
  readonly flash_sales: readonly FlashSale[];
  readonly pagination: Pagination;
}

export interface FlashSaleListParams extends ListParams {
  readonly status?: FlashSaleStatus | null;
}

export interface CreateFlashSaleInput {
  readonly name: string;
  readonly starts_at: string;
  readonly ends_at: string;
  readonly discount_percent: number;
  readonly product_ids: readonly string[];
}

// ---------------------------------------------------------------------------
// Bundles
// ---------------------------------------------------------------------------

export interface Bundle {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly price_fcfa: number;
  readonly original_price_fcfa: number;
  readonly savings_percent: number;
  readonly is_active: boolean;
  readonly product_count: number;
  readonly orders_count: number;
  readonly created_at: string;
}

export interface BundleListResponse {
  readonly bundles: readonly Bundle[];
  readonly pagination: Pagination;
}

export interface CreateBundleInput {
  readonly name: string;
  readonly description: string;
  readonly price_fcfa: number;
  readonly product_ids: readonly string[];
}

// ---------------------------------------------------------------------------
// Banners
// ---------------------------------------------------------------------------

export type BannerPosition =
  | "home_hero"
  | "home_mid"
  | "category"
  | "checkout";

export interface Banner {
  readonly id: string;
  readonly title: string;
  readonly image_url: string;
  readonly link_url: string;
  readonly position: BannerPosition;
  readonly is_active: boolean;
  readonly starts_at: string | null;
  readonly ends_at: string | null;
  readonly impressions: number;
  readonly clicks: number;
}

export interface BannerListResponse {
  readonly banners: readonly Banner[];
  readonly pagination: Pagination;
}

export interface BannerListParams extends ListParams {
  readonly position?: BannerPosition | null;
  readonly is_active?: boolean | null;
}

export interface CreateBannerInput {
  readonly title: string;
  readonly image_url: string;
  readonly link_url: string;
  readonly position: BannerPosition;
  readonly starts_at?: string | null;
  readonly ends_at?: string | null;
}

// ---------------------------------------------------------------------------
// Newsletter
// ---------------------------------------------------------------------------

export type SubscriberStatus = "active" | "unsubscribed";
export type CampaignStatus = "draft" | "scheduled" | "sent";

export interface NewsletterSubscriber {
  readonly id: string;
  readonly email: string;
  readonly name: string | null;
  readonly subscribed_at: string;
  readonly status: SubscriberStatus;
}

export interface NewsletterCampaign {
  readonly id: string;
  readonly subject: string;
  readonly status: CampaignStatus;
  readonly recipients_count: number;
  readonly open_rate: number;
  readonly scheduled_at: string | null;
  readonly sent_at: string | null;
}

export interface NewsletterListResponse {
  readonly subscribers: readonly NewsletterSubscriber[];
  readonly total: number;
  readonly active: number;
  readonly unsubscribed: number;
  readonly pagination: Pagination;
}

export interface CampaignListResponse {
  readonly campaigns: readonly NewsletterCampaign[];
  readonly pagination: Pagination;
}

export interface SubscriberListParams extends ListParams {
  readonly status?: SubscriberStatus | null;
  readonly q?: string | null;
}

export interface CreateCampaignInput {
  readonly subject: string;
  readonly scheduled_at?: string | null;
}

// ---------------------------------------------------------------------------
// Seasonal Calendar
// ---------------------------------------------------------------------------

export interface SeasonalCalendar {
  readonly id: string;
  readonly name: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly description: string;
  readonly linked_promotions: readonly string[];
}

export interface SeasonalListResponse {
  readonly events: readonly SeasonalCalendar[];
  readonly pagination: Pagination;
}

export interface CreateSeasonalInput {
  readonly name: string;
  readonly start_date: string;
  readonly end_date: string;
  readonly description: string;
  readonly linked_promotions?: readonly string[];
}

// ---------------------------------------------------------------------------
// Stacking Rules
// ---------------------------------------------------------------------------

export interface StackingRule {
  readonly id: string;
  readonly name: string;
  readonly allowed_types: readonly PromoType[];
  readonly max_stack: number;
  readonly priority_order: string;
  readonly is_active: boolean;
}

export interface StackingRuleListResponse {
  readonly rules: readonly StackingRule[];
  readonly pagination: Pagination;
}

export interface CreateStackingRuleInput {
  readonly name: string;
  readonly allowed_types: readonly PromoType[];
  readonly max_stack: number;
  readonly priority_order: string;
}

// ---------------------------------------------------------------------------
// Tracking Links
// ---------------------------------------------------------------------------

export interface TrackingLink {
  readonly id: string;
  readonly name: string;
  readonly url: string;
  readonly utm_source: string;
  readonly utm_medium: string;
  readonly utm_campaign: string;
  readonly clicks: number;
  readonly conversions: number;
  readonly revenue_fcfa: number;
  readonly created_at: string;
}

export interface TrackingLinkListResponse {
  readonly links: readonly TrackingLink[];
  readonly pagination: Pagination;
}

export interface TrackingLinkListParams extends ListParams {
  readonly q?: string | null;
}

export interface CreateTrackingLinkInput {
  readonly name: string;
  readonly url: string;
  readonly utm_source: string;
  readonly utm_medium: string;
  readonly utm_campaign: string;
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

export interface TopPromotion {
  readonly name: string;
  readonly uses: number;
  readonly discount_fcfa: number;
}

export interface PromoVsNoPromo {
  readonly with_promo_avg_order: number;
  readonly without_promo_avg_order: number;
}

export interface PromoAnalyticsSummary {
  readonly period: string;
  readonly total_discount_given_fcfa: number;
  readonly orders_with_promo: number;
  readonly avg_discount_percent: number;
  readonly top_promotions: readonly TopPromotion[];
  readonly promo_vs_no_promo: PromoVsNoPromo;
}

// ---------------------------------------------------------------------------
// API client
// ---------------------------------------------------------------------------

export const marketingApi = {
  promotions: {
    list: (params: PromotionListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        type: params.type,
        status: params.status,
        date_from: params.date_from,
        date_to: params.date_to,
      });
      return adminFetch<PromotionListResponse>(
        `/api/admin/marketing/promotions${qs}`,
      );
    },
    create: (input: CreatePromotionInput) =>
      adminFetch<Promotion>("/api/admin/marketing/promotions", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    update: (id: string, input: Partial<CreatePromotionInput>) =>
      adminFetch<Promotion>(`/api/admin/marketing/promotions/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    pause: (id: string) =>
      adminFetch<Promotion>(
        `/api/admin/marketing/promotions/${encodeURIComponent(id)}/pause`,
        { method: "POST" },
      ),
    resume: (id: string) =>
      adminFetch<Promotion>(
        `/api/admin/marketing/promotions/${encodeURIComponent(id)}/resume`,
        { method: "POST" },
      ),
    remove: (id: string) =>
      adminFetch<{ success: boolean; id: string }>(
        `/api/admin/marketing/promotions/${encodeURIComponent(id)}`,
        { method: "DELETE" },
      ),
  },

  coupons: {
    list: (params: CouponListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        status: params.status,
        promotion_id: params.promotion_id,
      });
      return adminFetch<CouponListResponse>(`/api/admin/marketing/coupons${qs}`);
    },
    create: (input: CreateCouponInput) =>
      adminFetch<Coupon>("/api/admin/marketing/coupons", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    bulkCreate: (input: BulkCreateCouponsInput) =>
      adminFetch<{ created: number }>("/api/admin/marketing/coupons/bulk", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    revoke: (id: string) =>
      adminFetch<Coupon>(
        `/api/admin/marketing/coupons/${encodeURIComponent(id)}/revoke`,
        { method: "POST" },
      ),
  },

  flashSales: {
    list: (params: FlashSaleListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        status: params.status,
      });
      return adminFetch<FlashSaleListResponse>(
        `/api/admin/marketing/flash-sales${qs}`,
      );
    },
    create: (input: CreateFlashSaleInput) =>
      adminFetch<FlashSale>("/api/admin/marketing/flash-sales", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    end: (id: string) =>
      adminFetch<FlashSale>(
        `/api/admin/marketing/flash-sales/${encodeURIComponent(id)}/end`,
        { method: "POST" },
      ),
  },

  bundles: {
    list: (params: ListParams = {}) => {
      const qs = toQueryString({ page: params.page, limit: params.limit });
      return adminFetch<BundleListResponse>(
        `/api/admin/marketing/bundles${qs}`,
      );
    },
    create: (input: CreateBundleInput) =>
      adminFetch<Bundle>("/api/admin/marketing/bundles", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    toggle: (id: string) =>
      adminFetch<Bundle>(
        `/api/admin/marketing/bundles/${encodeURIComponent(id)}/toggle`,
        { method: "POST" },
      ),
  },

  banners: {
    list: (params: BannerListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        position: params.position,
        is_active: params.is_active,
      });
      return adminFetch<BannerListResponse>(
        `/api/admin/marketing/banners${qs}`,
      );
    },
    create: (input: CreateBannerInput) =>
      adminFetch<Banner>("/api/admin/marketing/banners", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    update: (id: string, input: Partial<CreateBannerInput>) =>
      adminFetch<Banner>(`/api/admin/marketing/banners/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(input),
      }),
    toggle: (id: string) =>
      adminFetch<Banner>(
        `/api/admin/marketing/banners/${encodeURIComponent(id)}/toggle`,
        { method: "POST" },
      ),
  },

  newsletter: {
    subscribers: (params: SubscriberListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        status: params.status,
        q: params.q,
      });
      return adminFetch<NewsletterListResponse>(
        `/api/admin/marketing/newsletter/subscribers${qs}`,
      );
    },
    campaigns: (params: ListParams = {}) => {
      const qs = toQueryString({ page: params.page, limit: params.limit });
      return adminFetch<CampaignListResponse>(
        `/api/admin/marketing/newsletter/campaigns${qs}`,
      );
    },
    createCampaign: (input: CreateCampaignInput) =>
      adminFetch<NewsletterCampaign>(
        "/api/admin/marketing/newsletter/campaigns",
        { method: "POST", body: JSON.stringify(input) },
      ),
  },

  seasonal: {
    list: (params: ListParams = {}) => {
      const qs = toQueryString({ page: params.page, limit: params.limit });
      return adminFetch<SeasonalListResponse>(
        `/api/admin/marketing/seasonal${qs}`,
      );
    },
    create: (input: CreateSeasonalInput) =>
      adminFetch<SeasonalCalendar>("/api/admin/marketing/seasonal", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },

  stacking: {
    list: (params: ListParams = {}) => {
      const qs = toQueryString({ page: params.page, limit: params.limit });
      return adminFetch<StackingRuleListResponse>(
        `/api/admin/marketing/stacking-rules${qs}`,
      );
    },
    create: (input: CreateStackingRuleInput) =>
      adminFetch<StackingRule>("/api/admin/marketing/stacking-rules", {
        method: "POST",
        body: JSON.stringify(input),
      }),
    toggle: (id: string) =>
      adminFetch<StackingRule>(
        `/api/admin/marketing/stacking-rules/${encodeURIComponent(id)}/toggle`,
        { method: "POST" },
      ),
  },

  tracking: {
    list: (params: TrackingLinkListParams = {}) => {
      const qs = toQueryString({
        page: params.page,
        limit: params.limit,
        q: params.q,
      });
      return adminFetch<TrackingLinkListResponse>(
        `/api/admin/marketing/tracking-links${qs}`,
      );
    },
    create: (input: CreateTrackingLinkInput) =>
      adminFetch<TrackingLink>("/api/admin/marketing/tracking-links", {
        method: "POST",
        body: JSON.stringify(input),
      }),
  },

  analytics: {
    summary: (period: string) =>
      adminFetch<PromoAnalyticsSummary>(
        `/api/admin/marketing/analytics${toQueryString({ period })}`,
      ),
  },
} as const;
